import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:7001';

// Create axios instance with base configuration
const apiClient = axios.create({
  baseURL: `${API_BASE_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Token getter function - will be set by AuthContext
let getAccessTokenFn = null;

export const setAccessTokenGetter = (getter) => {
  getAccessTokenFn = getter;
};

apiClient.interceptors.request.use(async (config) => {
  if (getAccessTokenFn) {
    const token = await getAccessTokenFn();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

export const authAPI = {
  getCurrentUser: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },
};

// Analytics API
export const analyticsAPI = {
  getOverview: async (dateRange = '7d') => {
    const response = await apiClient.get(`/analytics/overview?dateRange=${dateRange}`);
    return response.data;
  },
  getHeatmap: async (dateRange = '7d') => {
    const response = await apiClient.get(`/analytics/heatmap?dateRange=${dateRange}`);
    return response.data;
  },
  getAnomalies: async () => {
    const response = await apiClient.get('/analytics/anomalies');
    return response.data;
  }
};

// Call Log API
export const callLogAPI = {
  getCallLog: async (filters = {}) => {
    const params = new URLSearchParams();

    if (filters.startDate) params.append('startDate', filters.startDate);
    if (filters.endDate) params.append('endDate', filters.endDate);
    if (filters.agentId) params.append('agentId', filters.agentId);
    if (filters.queueId) params.append('queueId', filters.queueId);
    if (filters.outcome) params.append('outcome', filters.outcome);
    if (filters.direction) params.append('direction', filters.direction);
    if (filters.searchTerm) params.append('searchTerm', filters.searchTerm);
    if (filters.page) params.append('page', filters.page);
    if (filters.pageSize) params.append('pageSize', filters.pageSize);
    if (filters.sortBy) params.append('sortBy', filters.sortBy);
    if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);

    const response = await apiClient.get(`/calllog?${params.toString()}`);
    return response.data;
  },
  getCallById: async (id) => {
    const response = await apiClient.get(`/calllog/${id}`);
    return response.data;
  },
  getCallJourney: async (id) => {
    const response = await apiClient.get(`/calllog/${id}/journey`);
    return response.data;
  },
  getCallRecording: async (id) => {
    const response = await apiClient.get(`/calllog/${id}/recording`);
    return response.data;
  },
  // Fetches a short-lived streaming token (60s TTL) for a specific recording
  getRecordingStreamToken: async (id) => {
    const response = await apiClient.get(`/calllog/${id}/recording/token`);
    return response.data;
  },
  // Returns the URL for streaming recording audio with a streaming token
  getRecordingStreamUrl: (id, streamingToken) => {
    return `${API_BASE_URL}/api/calllog/${id}/recording/stream?access_token=${encodeURIComponent(streamingToken)}`;
  }
};

// Agent API
export const agentAPI = {
  getAgents: async () => {
    const response = await apiClient.get('/agent');
    return response.data;
  },
  getAgentAnalytics: async (dateRange = '7d') => {
    const response = await apiClient.get(`/agent/analytics?dateRange=${dateRange}`);
    return response.data;
  },
  getAgentDetail: async (agentId, dateRange = '30d') => {
    const response = await apiClient.get(`/agent/${agentId}/analytics?dateRange=${dateRange}`);
    return response.data;
  }
};

// Queue API
export const queueAPI = {
  getQueues: async () => {
    const response = await apiClient.get('/queue');
    return response.data;
  },
  getQueueAnalytics: async (dateRange = '7d') => {
    const response = await apiClient.get(`/queue/analytics?dateRange=${dateRange}`);
    return response.data;
  },
  getQueueDetail: async (queueId, dateRange = '30d') => {
    const response = await apiClient.get(`/queue/${queueId}/analytics?dateRange=${dateRange}`);
    return response.data;
  }
};

// Wallboard API
export const wallboardAPI = {
  getCurrentMetrics: async () => {
    const response = await apiClient.get('/wallboard/current');
    return response.data;
  }
};

// Hierarchy configuration (docs/design/contracts.md §7, A009)
export const configAPI = {
  getHierarchyLevels: async () => {
    const response = await apiClient.get('/config/hierarchy/levels');
    return response.data;
  },
  getHierarchyTree: async () => {
    const response = await apiClient.get('/config/hierarchy/tree');
    return response.data;
  },
  getCommunities: async () => {
    const response = await apiClient.get('/config/communities');
    return response.data;
  },
};

// Signed-in user's filter options (contracts.md §7, G1 review): { unrestricted, levels, tree, communities },
// already limited to the user's data scope. Any signed-in role.
export const meAPI = {
  getFilterOptions: async () => {
    const response = await apiClient.get('/me/filter-options');
    return response.data;
  },
};

// Shell status: { accountName, lastCallRecordSyncAt, attention: { insights } }.
// Requested as a contract change (A011 completion output); not in contracts.md yet.
export const shellAPI = {
  getStatus: async () => {
    const response = await apiClient.get('/shell/status');
    return response.data;
  },
};

// Development API
export const devAPI = {
  seedDatabase: async () => {
    const response = await apiClient.post('/dev/seed');
    return response.data;
  }
};

export default apiClient;
